/**
 * Per-workspace accent + theme presets (white-label theming).
 * The accent is written as CSS variables on <html>, so it wins over both the
 * light and dark token blocks without touching a single component.
 */

export const THEME_PRESETS = [
  { id: "jade", label: "Mahano Jade", accent: "#30B5A1", surface: "#E7DDD0" },
  { id: "indigo", label: "Deep Indigo", accent: "#5566E8", surface: "#DCDFF2" },
  { id: "clay", label: "Terracotta", accent: "#C4603F", surface: "#EEDCD2" },
  { id: "forest", label: "Deep Forest", accent: "#3C8C5C", surface: "#DCE7DC" },
  { id: "graphite", label: "Graphite", accent: "#53606E", surface: "#E2E4E7" },
];

const ACCENT_VARS = ["--primary", "--ring", "--studio-primary", "--halo-blue", "--halo-teal", "--mahano-jade", "--chart-1"];
const SURFACE_VARS = ["--secondary", "--accent", "--mahano-sand"];

export function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  const r = ((int >> 16) & 255) / 255, g = ((int >> 8) & 255) / 255, b = (int & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0, s = 0;
  if (d) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

const str = ({ h, s, l }) => `${h} ${s}% ${l}%`;
const shift = (hsl, delta) => ({ ...hsl, l: Math.min(96, Math.max(6, hsl.l + delta)) });

const INK_FG = "221 34% 11%";
const WHITE_FG = "0 0% 100%";

function hslToRgb({ h, s, l }) {
  const S = s / 100, L = l / 100;
  const c = (1 - Math.abs(2 * L - 1)) * S;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = L - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return [r + m, g + m, b + m];
}

const luminance = (hsl) => {
  const lin = hslToRgb(hsl).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
};

const contrast = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/** Pick whichever label colour actually reads on this accent (WCAG, not a lightness guess). */
function readableForeground(accentHsl) {
  const white = { h: 0, s: 0, l: 100 };
  const ink = { h: 221, s: 34, l: 11 };
  return contrast(accentHsl, white) >= contrast(accentHsl, ink) ? WHITE_FG : INK_FG;
}

/** Nudge the fill until its best label colour clears WCAG AA. */
function legibleAccent(hsl, dark) {
  let out = hsl;
  for (let i = 0; i < 8; i += 1) {
    const best = Math.max(contrast(out, { h: 0, s: 0, l: 100 }), contrast(out, { h: 221, s: 34, l: 11 }));
    if (best >= 4.5) break;
    out = shift(out, dark ? 6 : -4);
  }
  return out;
}

export function applyBrandTheme({ accent, surface, dark }) {
  const root = document.documentElement;
  const base = hexToHsl(accent);
  if (base) {
    // Dark surfaces need a slightly lighter accent to hold the same presence.
    const tuned = legibleAccent(dark ? shift(base, 7) : base, dark);
    ACCENT_VARS.forEach((v) => root.style.setProperty(v, str(tuned)));
    root.style.setProperty("--studio-primary-hover", str(shift(tuned, dark ? 6 : -6)));
    root.style.setProperty("--studio-secondary", str(shift(base, dark ? 10 : -13)));
    root.style.setProperty("--halo-violet", str(shift(base, dark ? 10 : -13)));
    root.style.setProperty("--primary-foreground", readableForeground(tuned));
  }
  const surf = hexToHsl(surface);
  if (surf && !dark) {
    SURFACE_VARS.forEach((v) => root.style.setProperty(v, str(shift(surf, 3))));
  } else {
    SURFACE_VARS.forEach((v) => root.style.removeProperty(v));
  }
}

export function clearBrandTheme() {
  const root = document.documentElement;
  [...ACCENT_VARS, ...SURFACE_VARS, "--studio-primary-hover", "--studio-secondary",
   "--halo-violet", "--primary-foreground"].forEach((v) => root.style.removeProperty(v));
}
